import React, { useState, useEffect, useRef } from 'react';
import { Modal, Box, Typography, Button, CircularProgress } from '@mui/material';
import { supabase } from '../supabaseClient';
import { API_URL } from '../globals';

const SubscriptionCheckWrapper = ({ children }) => {
  const [isSubscribed, setIsSubscribed] = useState(true);
  const [open, setOpen] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [isGeneratingCheckout, setIsGeneratingCheckout] = useState(false);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const generatePromiseRef = useRef(null);

  const handleClose = () => setOpen(false);

  const generateCheckoutUrl = async () => {
    // Reuse in-flight request if one exists
    if (generatePromiseRef.current) {
      return generatePromiseRef.current;
    }

    const promise = (async () => {
      try {
        setIsGeneratingCheckout(true);
        const { data: { session } } = await supabase.auth.getSession();
        const authToken = session?.access_token;
        if (!authToken) return null;

        const response = await fetch(`${API_URL}/stripe/generate_checkout_url`, {
          method: 'GET',
          headers: new Headers({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`
          }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data?.url) {
            setCheckoutUrl(data.url);
            return data.url;
          }
        }
      } catch (error) {
        console.error('Failed to generate checkout URL', error);
      } finally {
        setIsGeneratingCheckout(false);
      }
      return null;
    })();

    generatePromiseRef.current = promise;
    // Ensure ref is cleared after completion
    promise.finally(() => {
      generatePromiseRef.current = null;
    });
    return promise;
  };

  const handleCheckoutClick = async () => {
    setIsButtonLoading(true);
    try {
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }
      // Await any in-flight generation, or trigger one
      const url = await generateCheckoutUrl();
      if (url) {
        window.location.href = url;
      }
    } finally {
      setIsButtonLoading(false);
    }
  };

  useEffect(() => {
    const fetchUserDetails = async () => {
      const user = (await supabase.auth.getUser()).data.user;
      if (user && user.email) {
        setUserEmail(user.email);
      }
    };
    

    fetchUserDetails();
    const checkSubscription = async () => {
      const authToken = (await supabase.auth.getSession()).data.session.access_token;
      const response = await fetch(API_URL+'/user/check_subscription', {
        method: 'GET',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setIsSubscribed(data.subscribed);
        if (!data.subscribed) {
          setOpen(true);
        }
      } else {
        console.error('Failed to fetch the subscription status');
        setIsSubscribed(false);
        setOpen(true);
      }
    };
    checkSubscription();

  }, []);

  useEffect(() => {
    if (open && !checkoutUrl) {
      generateCheckoutUrl();
    }
  }, [open]);

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 600,
    bgcolor: '#1E1E1E',
    borderRadius: 2,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.24)',
    p: 4,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    border: '1px solid #333',
  };

  return (
    <>
      {!isSubscribed && (
        <Modal 
          open={open} 
          aria-labelledby="subscription-modal-title"
          aria-describedby="subscription-modal-description"
        >
          <Box sx={modalStyle}>
            <Typography 
              id="subscription-modal-title" 
              variant="h5" 
              component="h2" 
              align="center"
              sx={{ 
                color: '#fff',
                fontWeight: 600,
                mb: 2
              }}
            >
              No Active Subscription
            </Typography>
            <Typography 
              id="subscription-modal-description" 
              variant="body1" 
              sx={{ 
                mt: 2,
                color: '#888',
                textAlign: 'center',
                maxWidth: '80%',
                mb: 3
              }}
            >
              Your account does not have a valid subscription. Please subscribe to continue using Fume.
            </Typography>
            <Button 
              onClick={handleCheckoutClick}
              variant="contained"
              sx={{
                mt: 2,
                bgcolor: '#2e4e41',
                color: '#fff',
                px: 4,
                py: 1,
                '&:hover': {
                  bgcolor: '#3a6351'
                },
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              {isButtonLoading && (
                <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
              )}
              Go to Payment
            </Button>
          </Box>
        </Modal>
      )}
      {isSubscribed && children}
    </>
  );
};

export default SubscriptionCheckWrapper;
